import type { VideoDetailType } from "@volgo/core/contract";
import type Hls from "hls.js";
import { useEffect, useRef, useState } from "react";
import { useSession } from "../session";
import { readPref, writePref } from "../ui/prefs";
import { time } from "./shared";

export function playerKey(video: HTMLVideoElement, key: string, fullscreen: () => void) {
  const seek = (seconds: number) => {
    video.currentTime = Math.max(0, Math.min(Number.isFinite(video.duration) ? video.duration : 0, seconds));
  };
  switch (key.toLowerCase()) {
    case " ":
    case "k":
      if (video.paused) void video.play().catch(() => {});
      else video.pause();
      break;
    case "arrowleft":
      seek(video.currentTime - 5);
      break;
    case "arrowright":
      seek(video.currentTime + 5);
      break;
    case "j":
      seek(video.currentTime - 10);
      break;
    case "l":
      seek(video.currentTime + 10);
      break;
    case "arrowup":
      video.volume = Math.min(1, video.volume + 0.05);
      break;
    case "arrowdown":
      video.volume = Math.max(0, video.volume - 0.05);
      break;
    case "m":
      video.muted = !video.muted;
      break;
    case "f":
      fullscreen();
      break;
    default:
      if (/^[0-9]$/.test(key)) seek((video.duration * Number(key)) / 10);
      else return false;
  }
  return true;
}
export function Player({ video, onEnded }: { video: VideoDetailType; onEnded: () => void }) {
  const { client } = useSession();
  const media = useRef<HTMLVideoElement>(null);
  const pointer = useRef("");
  const root = useRef<HTMLElement>(null);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(video.durationMs ?? 0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [controls, setControls] = useState(true);
  const [restart, setRestart] = useState(false);
  const [levels, setLevels] = useState<{ height: number }[]>([]);
  const hls = useRef<Hls | null>(null);
  const hide = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const playlist = "playlist" in video ? video.playlist : undefined;
  const reveal = () => {
    setControls(true);
    clearTimeout(hide.current);
    hide.current = setTimeout(() => {
      if (
        media.current &&
        !media.current.paused &&
        !document.activeElement?.closest(
          ".video-controls button, .video-controls input, .video-controls select",
        )
      )
        setControls(false);
    }, 2500);
  };
  const fullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void root.current?.requestFullscreen?.();
  };
  useEffect(() => {
    const element = media.current;
    if (!element || !playlist || error) return;
    let actualMs = 0,
      lastTick = performance.now(),
      viewed = false,
      lastSave = 0,
      running = false;
    const save = (keepalive = false) => {
      const pos = Math.round(element.currentTime * 1000);
      if (!Number.isFinite(pos)) return;
      if (keepalive) void client.saveVideoPosition(video.id, pos).catch(() => {});
      else
        void client
          .call("videos.progress", { params: { id: video.id }, body: { positionMs: pos } })
          .catch(() => {});
    };
    const account = () => {
      const now = performance.now();
      if (running && !element.seeking && !element.paused) actualMs += Math.min(now - lastTick, 1500);
      lastTick = now;
      if (!viewed && actualMs >= 10000) {
        viewed = true;
        void client.call("videos.view", { params: { id: video.id } }).catch(() => {});
      }
    };
    const start = () => {
      lastTick = performance.now();
      running = true;
    };
    const waiting = () => {
      account();
      running = false;
    };
    const pause = () => {
      waiting();
      save();
    };
    const leave = () => save(true);
    const visibility = () => {
      if (document.hidden) save(true);
    };
    const timer = setInterval(() => {
      account();
      if (running && actualMs - lastSave >= 10000) {
        lastSave = actualMs;
        save();
      }
    }, 250);
    element.addEventListener("playing", start);
    element.addEventListener("waiting", waiting);
    element.addEventListener("seeking", waiting);
    element.addEventListener("pause", pause);
    element.addEventListener("ended", pause);
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      save(true);
      clearInterval(timer);
      element.removeEventListener("playing", start);
      element.removeEventListener("waiting", waiting);
      element.removeEventListener("seeking", waiting);
      element.removeEventListener("pause", pause);
      element.removeEventListener("ended", pause);
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [client, playlist, video.id, error]);
  useEffect(() => {
    const element = media.current;
    if (!element || !playlist || error) return;
    let alive = true;
    try {
      const pref = JSON.parse(readPref("volgo.player") ?? "{}");
      if (typeof pref.volume === "number" && pref.volume >= 0 && pref.volume <= 1)
        element.volume = pref.volume;
      element.muted = pref.muted === true;
    } catch {
      /* Defaults survive invalid storage. */
    }
    const native =
      /Safari/.test(navigator.userAgent) &&
      !/Chrome|Chromium|CriOS|Edg|Android/.test(navigator.userAgent) &&
      element.canPlayType("application/vnd.apple.mpegurl");
    if (native) element.src = playlist;
    else
      import("hls.js")
        .then(({ default: HlsClass }) => {
          if (!alive) return;
          if (!HlsClass.isSupported()) {
            setError(true);
            return;
          }
          const instance = new HlsClass({
            xhrSetup: (request) => {
              request.withCredentials = true;
            },
            fetchSetup: (context, init) => new Request(context.url, { ...init, credentials: "include" }),
          });
          hls.current = instance;
          instance.on(HlsClass.Events.MANIFEST_PARSED, () => setLevels(instance.levels));
          instance.on(HlsClass.Events.ERROR, (_event, data) => {
            if (data.fatal) setError(true);
          });
          instance.loadSource(playlist);
          instance.attachMedia(element);
        })
        .catch(() => alive && setError(true));
    return () => {
      alive = false;
      hls.current?.destroy();
      hls.current = null;
      element.removeAttribute("src");
      element.load();
      clearTimeout(hide.current);
    };
  }, [playlist, error]);
  useEffect(() => {
    if (!restart) return;
    const timer = setTimeout(() => setRestart(false), 3000);
    return () => clearTimeout(timer);
  }, [restart]);
  if (error)
    return (
      <div className="video-playback-error" role="alert">
        이 기기에서 재생할 수 없는 영상입니다(코덱: {video.videoCodec?.toUpperCase() ?? "알 수 없음"})
      </div>
    );
  return (
    <section
      ref={root}
      className="video-player"
      aria-label="영상 플레이어"
      // biome-ignore lint/a11y/noNoninteractiveTabindex: player region owns the documented playback shortcuts
      tabIndex={0}
      onMouseMove={reveal}
      onFocus={reveal}
      onKeyDown={(e) => {
        reveal();
        if (e.target instanceof HTMLElement && e.target.closest("input, select, button")) return;
        if (media.current && playerKey(media.current, e.key, fullscreen)) e.preventDefault();
      }}
    >
      {/* No captions are supplied by uploaders in this scope. */}
      {/* biome-ignore lint/a11y/useMediaCaption: user uploaded video has no subtitle track contract */}
      <video
        ref={media}
        crossOrigin="use-credentials"
        playsInline
        preload="metadata"
        poster={video.policy.level === "none" && "poster" in video ? (video.poster ?? undefined) : undefined}
        onPointerDown={(e) => {
          pointer.current = e.pointerType;
        }}
        onPointerUp={(e) => {
          if (e.pointerType === "touch") setControls((v) => !v);
        }}
        onClick={(e) => {
          if (pointer.current === "touch" && e.detail !== 0) return;
          if (media.current) playerKey(media.current, "k", fullscreen);
        }}
        onError={() => setError(true)}
        onPlay={() => {
          setPlaying(true);
          reveal();
        }}
        onPause={() => {
          setPlaying(false);
          setControls(true);
        }}
        onEnded={onEnded}
        onVolumeChange={(e) => {
          setVolume(e.currentTarget.volume);
          setMuted(e.currentTarget.muted);
          writePref(
            "volgo.player",
            JSON.stringify({ volume: e.currentTarget.volume, muted: e.currentTarget.muted }),
          );
        }}
        onLoadedMetadata={(e) => {
          const m = e.currentTarget;
          setDuration(m.duration * 1000);
          if (video.progress && !video.progress.completed && video.progress.positionMs > 0) {
            m.currentTime = video.progress.positionMs / 1000;
            setRestart(true);
          }
        }}
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime * 1000)}
        onProgress={(e) => {
          const m = e.currentTarget;
          if (m.buffered.length) setBuffered(m.buffered.end(m.buffered.length - 1) * 1000);
        }}
      />
      {restart && (
        <button
          className="video-restart"
          type="button"
          onClick={() => {
            if (media.current) media.current.currentTime = 0;
            setRestart(false);
          }}
        >
          처음부터
        </button>
      )}
      <div className="video-controls" data-hidden={!controls && playing ? "true" : undefined}>
        <div className="video-seek">
          <progress value={buffered} max={duration || 1} aria-label="버퍼" />
          <input
            aria-label="재생 위치"
            type="range"
            min={0}
            max={duration || 1}
            value={position}
            onChange={(e) => {
              if (media.current) media.current.currentTime = Number(e.target.value) / 1000;
            }}
          />
        </div>
        <div className="video-control-row">
          <button
            type="button"
            aria-label={playing ? "일시정지" : "재생"}
            onClick={() => {
              if (media.current) playerKey(media.current, "k", fullscreen);
            }}
          >
            {playing ? "Ⅱ" : "▶"}
          </button>
          <button
            type="button"
            aria-label={muted ? "음소거 해제" : "음소거"}
            onClick={() => {
              if (media.current) media.current.muted = !media.current.muted;
            }}
          >
            {muted ? "무음" : "소리"}
          </button>
          <input
            type="range"
            className="video-volume"
            aria-label="음량"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => {
              if (media.current) media.current.volume = Number(e.target.value);
            }}
          />
          <span>
            {time(position)} / {time(duration)}
          </span>
          <span className="video-resolution">{video.height}p</span>
          {levels.length > 1 && (
            <select
              aria-label="화질"
              onChange={(e) => {
                if (hls.current) hls.current.currentLevel = Number(e.target.value);
              }}
            >
              <option value={-1}>자동</option>
              {levels.map((l, i) => (
                <option key={l.height} value={i}>
                  {l.height}p
                </option>
              ))}
            </select>
          )}
          <button type="button" onClick={fullscreen} aria-label="전체 화면">
            ⛶
          </button>
        </div>
      </div>
    </section>
  );
}
